/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {
    ENTITY_DEFAULT_PROPS,
    EntityFormProps,
    EntityFormState,
    EntityForm,
    EntityStatus,
} from './EntityForm';
import {ProvidersContext} from './commons';

/**
 * Component for rendering an entity
 */
export class DefaultEntityForm extends EntityForm<
    EntityFormProps,
    EntityFormState
> {
    public static contextType = ProvidersContext;
    public context!: React.ContextType<typeof ProvidersContext>;
    public static defaultProps = ENTITY_DEFAULT_PROPS;
    state: EntityFormState;
    _isMounted: boolean;
    _currentLoadId: number;

    constructor(props: Readonly<EntityFormProps>) {
        super(props);
        this._isMounted = false;
        this._currentLoadId = -1;
        this.state = {
            statuts: EntityStatus.loading,
            data: null,
            schema: null,
        } as any;
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadData();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(
        prevProps: EntityFormProps,
        prevState: EntityFormState,
        snapshot: any
    ) {
        if (this.props.eid !== prevProps.eid) {
            this.setState({
                status: EntityStatus.loading,
                data: null,
                schema: null,
            });
            this.loadData();
        } else if (this.props.timestamp !== prevProps.timestamp) {
            this.loadData();
        }
    }

    /**
     * Loads the entity data and schema
     */
    loadData = () => {
        // increment load id and begins loading with this identifier
        // this is safe because of the javascript's GIL
        this._currentLoadId += 1;
        this.doLoad(this._currentLoadId);
    };

    /**
     * Checks whether the current load operation must stop
     * A load operation may take time and thus it may become
     * dangerous, or superseded by another one in the following cases:
     * - the component has been unmounted, we cannot update the state anymore
     * - this is no longer the latest load operation, another one has been launched
     * @param loadId The identifier of the load operation
     * @returns true if the load operation must stop
     */
    stopLoadOp = (loadId: number) => {
        return !this._isMounted || this._currentLoadId !== loadId;
    };

    /**
     * Executes a load operation with the specified identifier
     * @param loadId The identifier of this load operation
     */
    doLoad = async (loadId: number) => {
        this.setState({
            status: EntityStatus.loading,
            error: null,
        });
        try {
            const schema = await this.context.schemaProvider.getEntitySchemaFor(
                this.props.eid
            );
            if (this.stopLoadOp(loadId)) return;
            this.setState({
                status:
                    schema !== null
                        ? EntityStatus.loading
                        : EntityStatus.nodata,
                schema: schema,
            });
            if (schema === null) {
                return;
            }
            const data = await this.context.entityProvider.getEntityGivenSchema(
                this.props.eid,
                schema
            );
            if (this.stopLoadOp(loadId)) return;
            this.setState({
                status:
                    data !== null ? EntityStatus.withdata : EntityStatus.nodata,
                data: data,
            });
        } catch (reason) {
            this.setState({
                status: EntityStatus.onerror,
                error: reason,
                data: null,
                schema: null,
            });
        }
    };

    render(): JSX.Element {
        return this.props.render(this);
    }
}
